import {
	type EmptyProfile,
	type Profile,
	useMeProfileGetMeProfile,
} from '@cocoonspace/api'
import { APP_CONFIG } from '@cocoonspace/shared/config'
import { useCopyClipboard } from '@cocoonspace/shared/hooks/use-copy-clipboard.hook'
import { Button } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@cocoonspace/ui/components/dialog'
import { Skeleton } from '@cocoonspace/ui/components/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@cocoonspace/ui/components/tooltip'
import { useLingui } from '@lingui/react/macro'
import { ClipboardIcon, CodeXmlIcon, LinkIcon } from 'lucide-react'
import { toast } from 'sonner'
import { highlight } from 'sugar-high'

import { UserAvatar } from '~/components/user-avatar'
import { useLocale } from '~/hooks/use-locale.hook'
import type { Locale } from '~/i18n/routing'
import { ProfileEditDialog } from '../public-page/profile-edit-dialog'

const isProfile = (profile: EmptyProfile | Profile): profile is Profile =>
	!('isEmpty' in profile)

export const ProfileCardSkeleton = () => (
	<Card className='flex items-center gap-6 p-6'>
		<Skeleton className='size-20 rounded-full shadow-lg' />

		<div className='flex flex-1 flex-col gap-2'>
			<Skeleton className='h-7 w-[220px]' />
			<div className='flex flex-col gap-1'>
				<Skeleton className='h-4 w-[320px]' />
				<Skeleton className='h-4 w-[260px]' />
			</div>
		</div>

		<div className='flex items-center gap-8'>
			<Skeleton className='h-9 w-[110px] rounded' />
			<Skeleton className='h-4 w-[140px] rounded' />
		</div>
	</Card>
)

const getProfileEmbedCode = (profileSlug: string, locale: Locale) =>
	`<script
	src="${APP_CONFIG.appUrl}/embed.v1.js"
	data-cocoon-profile-slug="${profileSlug}"
	data-locale="${locale}"
	async
></script>`

const CopyProfileLinkAction = ({ profileSlug }: { profileSlug: string }) => {
	const { t } = useLingui()
	const { copyToClipboard } = useCopyClipboard()

	const copyUrl = () => {
		const url = `${window.location.origin}/${profileSlug}`

		return copyToClipboard(url).then(() => {
			toast.success(t`URL copiée`)
		})
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button
					variant='ghost'
					size='sm'
					icon
					onClick={copyUrl}
				>
					<LinkIcon />
				</Button>
			</TooltipTrigger>

			<TooltipContent>{t`Copier l'URL`}</TooltipContent>
		</Tooltip>
	)
}

const EmbedCode = ({ code, title }: { code: string; title: string }) => {
	const { t } = useLingui()
	const { copyToClipboard, isCopied } = useCopyClipboard()

	return (
		<section className='flex flex-col gap-3'>
			<h3 className='font-semibold'>{title}</h3>

			<pre className='embed-code max-h-48 overflow-auto rounded-md bg-slate-950 p-4 text-sm text-slate-50'>
				<code
					dangerouslySetInnerHTML={{
						__html: highlight(code, { lang: 'html' }),
					}}
				/>
			</pre>

			<Button
				size='sm'
				onClick={() => copyToClipboard(code)}
			>
				<ClipboardIcon />
				{isCopied ? t`Code copié` : t`Copier le code`}
			</Button>
		</section>
	)
}

const EmbedSchedulingDialog = ({ profileSlug }: { profileSlug: string }) => {
	const { t } = useLingui()
	const locale = useLocale()

	return (
		<Dialog>
			<Tooltip>
				<TooltipTrigger asChild>
					<DialogTrigger asChild>
						<Button
							variant='ghost'
							size='sm'
							icon
						>
							<CodeXmlIcon />
						</Button>
					</DialogTrigger>
				</TooltipTrigger>

				<TooltipContent>{t`Intégrer sur mon site`}</TooltipContent>
			</Tooltip>

			<DialogContent className='max-w-2xl'>
				<DialogHeader>
					<DialogTitle className='text-2xl'>
						{t`Intégrer sur mon site`}
					</DialogTitle>

					<DialogDescription>
						{t`Copiez le code correspondant au contenu que vous souhaitez afficher sur votre site.`}
					</DialogDescription>
				</DialogHeader>

				<EmbedCode
					code={getProfileEmbedCode(profileSlug, locale)}
					title={t`Intégrer mon profil`}
				/>
			</DialogContent>
		</Dialog>
	)
}

export const ProfileCard = () => {
	const { data: meProfile, isLoading } = useMeProfileGetMeProfile({
		query: { retry: false },
	})

	if (isLoading) return <ProfileCardSkeleton />
	if (!meProfile || !isProfile(meProfile)) return null

	return (
		<Card className='flex flex-col items-center gap-6 p-6 md:flex-row'>
			<UserAvatar
				size='xl'
				avatarUrl={meProfile.avatarUrl ?? null}
			/>

			<div className='flex flex-1 flex-col'>
				<div className='text-center text-2xl font-bold md:text-left'>
					{meProfile.title}
				</div>

				<div className='text-muted-foreground text-center text-sm whitespace-pre-line md:text-left'>
					{meProfile.description}
				</div>
			</div>

			<div className='flex flex-wrap items-center justify-center gap-3 md:justify-end'>
				{meProfile.slug && (
					<>
						<CopyProfileLinkAction profileSlug={meProfile.slug} />
						<EmbedSchedulingDialog profileSlug={meProfile.slug} />
					</>
				)}

				<ProfileEditDialog icon />
			</div>
		</Card>
	)
}
